import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, User, Pen, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FOOTER_STYLES = [
  {
    id: "warm",
    label: "Warm",
    emoji: "🤗",
    preview: "Wishing your child continued success. Every step counts!",
  },
  {
    id: "motivational",
    label: "Motivational",
    emoji: "🚀",
    preview: "Keep up the great work — champions are made one lesson at a time!",
  },
  {
    id: "encouraging",
    label: "Encouraging",
    emoji: "🌟",
    preview: "Your child is doing wonderfully — keep encouraging them at home!",
  },
  {
    id: "formal",
    label: "Formal",
    emoji: "📋",
    preview: "Thank you for your continued support.",
  },
];

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sigName, setSigName] = useState("");
  const [footerStyle, setFooterStyle] = useState("warm");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        setSigName(r.data.signature_name || r.data.name || "");
        setFooterStyle(r.data.footer_style || "warm");
      })
      .catch(() => navigate("/login"));
  }, [navigate]);

  if (!user) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", {
        signature_name: sigName,
        footer_style: footerStyle,
      });
      setUser(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const selected = FOOTER_STYLES.find((f) => f.id === footerStyle) || FOOTER_STYLES[0];

  let buttonContent;
  if (saved) {
    buttonContent = (
      <span className="flex items-center gap-2">
        <Check className="h-4 w-4" /> Saved
      </span>
    );
  } else if (saving) {
    buttonContent = "Saving…";
  } else {
    buttonContent = "Save profile";
  }

  return (
    <main data-testid="profile-page" className="min-h-screen bg-[#FDF3EC]">
      <TopBar user={user} />
      <div className="mx-auto w-full max-w-xl px-5 py-10 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link
            to="/dashboard"
            className="mb-6 inline-flex items-center gap-1.5 text-sm text-[#8A6D5C] transition-colors hover:text-[#E8542A]"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to sessions
          </Link>

          <h1 data-testid="profile-title" className="font-display text-3xl font-bold text-[#1A1918] md:text-4xl">
            Teacher profile
          </h1>
          <p className="mt-1 text-sm text-[#8A6D5C]">
            Your name and footer style appear in every parent update you generate
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          onSubmit={save}
          className="mt-8 space-y-8 rounded-3xl border border-black/5 bg-white p-7 shadow-sm"
        >
          {/* Avatar row */}
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E8542A]/10">
              <User className="h-6 w-6 text-[#E8542A]" />
            </div>
            <div>
              <p className="font-semibold text-[#1A1918]">{user.name}</p>
              <p className="text-sm text-[#8A6D5C]">{user.mobile}</p>
            </div>
          </div>

          <div className="h-[1px] bg-[#F3EFEA]" />

          {/* Signature name */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#8A6D5C]">
              Signature name
            </Label>
            <p className="mt-0.5 text-xs text-[#A8968E]">
              This appears as "— Name" at the end of every parent update
            </p>
            <div className="relative mt-2">
              <Pen className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A6D5C]" />
              <Input
                data-testid="signature-name-input"
                value={sigName}
                onChange={(e) => setSigName(e.target.value)}
                placeholder="e.g. Sunita Patil, Ms. Patil"
                className="rounded-xl border-[#E8D5C8] pl-10 py-5 focus:border-[#E8542A]"
              />
            </div>
          </div>

          {/* Footer style */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#8A6D5C]">
              Message footer style
            </Label>
            <p className="mt-0.5 text-xs text-[#A8968E]">
              Sets the tone of the closing line in AI-generated parent updates
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2.5">
              {FOOTER_STYLES.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`footer-style-${f.id}`}
                  onClick={() => setFooterStyle(f.id)}
                  className={`relative flex flex-col items-start rounded-2xl border p-4 text-left transition-all duration-150 ${
                    footerStyle === f.id
                      ? "border-[#E8542A] bg-[#E8542A]/5 ring-1 ring-[#E8542A]/20"
                      : "border-[#E8D5C8] hover:border-[#E8542A]/40"
                  }`}
                >
                  {footerStyle === f.id && (
                    <div className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[#E8542A]">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                  )}
                  <span className="text-lg">{f.emoji}</span>
                  <span className="mt-1 text-sm font-semibold text-[#1A1918]">{f.label}</span>
                </button>
              ))}
            </div>

            {/* Live preview */}
            <motion.div
              key={footerStyle}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              data-testid="footer-preview"
              className="mt-4 rounded-2xl bg-[#F3EFEA] px-5 py-4"
            >
              <p className="text-xs font-bold uppercase tracking-wider text-[#8A6D5C]">Preview</p>
              <p className="mt-2 font-mono text-sm text-[#1A1918]">
                — {sigName || user.name}
              </p>
              <p className="font-mono text-sm italic text-[#8A6D5C]">{selected.preview}</p>
            </motion.div>
          </div>

          <Button
            type="submit"
            data-testid="save-profile-btn"
            disabled={saving}
            className="w-full rounded-2xl bg-[#E8542A] py-5 text-sm font-bold text-white hover:bg-[#C94520]"
          >
            {buttonContent}
          </Button>
        </motion.form>
      </div>
    </main>
  );
}
